import type { ReadonlyDeep } from 'type-fest';

import {
  getFragmentData,
  ResolvableServiceUsageInput,
  ServiceUsageOriginInput,
} from '@e-socna/data-layer';
import { EMPTY_OBJECT } from '@e-socna/empty';
import { invariant } from '@e-socna/utils';

import { pruneInput } from '../../../utils/prune-input.util';

import { resolvableQuantityForm2ApiMapper } from '../../ResolvableField/mappers/resolvable-quantity.form2api.mapper';
import { ResolvableServiceForm_ResolvableServiceBaseInterface_Fragment } from '../../ResolvableService/fragments/resolvable-service-base-interface.fragment';
import type {
  ResolvableServiceForm_ResolvableServiceBaseInterface,
  ResolvableServiceForm_ResolvableServiceBaseInterfaceData,
} from '../../ResolvableService/fragments/resolvable-service-base-interface.fragment';
import { resolvableServiceForm2ApiMapper } from '../../ResolvableService/mappers/resolvable-service.form2api.mapper';

import type { ResolvableServiceUsageForm_ResolvableServiceUsage } from '../fragments/resolvable-service-usage.fragment';

import { resolvableServiceUsageSettingsForm2ApiMapper } from './resolvable-service-usage-settings.form2api.mapper';

// Writing the service usages requires reading their amounts (the update of a mission and its
// amounts read go to the same roles).
const withReadableValue = (
  specialService: ResolvableServiceForm_ResolvableServiceBaseInterfaceData,
): ResolvableServiceForm_ResolvableServiceBaseInterface => {
  const { value } = specialService;

  invariant(!!value, 'A special service is saved with its readable amounts');

  return { ...specialService, value };
};

export function resolvableServiceUsageForm2ApiMapper(
  resolvableServiceUsage: ReadonlyDeep<ResolvableServiceUsageForm_ResolvableServiceUsage>,
): Readonly<ResolvableServiceUsageInput> {
  const resolvableServiceUsageInput = pruneInput(
    resolvableServiceUsage,
    ResolvableServiceUsageInput,
  );

  const specialService = !resolvableServiceUsage.specialService
    ? resolvableServiceUsage.specialService
    : getFragmentData(
        ResolvableServiceForm_ResolvableServiceBaseInterface_Fragment,
        resolvableServiceUsage.specialService,
      );

  return {
    ...resolvableServiceUsageInput,
    ...(resolvableServiceUsage.settings
      ? {
          settings: resolvableServiceUsageSettingsForm2ApiMapper(
            resolvableServiceUsage.settings,
          ),
        }
      : EMPTY_OBJECT),
    ...(resolvableServiceUsage.quantity
      ? {
          quantity: resolvableQuantityForm2ApiMapper(
            resolvableServiceUsage.quantity,
          ),
        }
      : EMPTY_OBJECT),
    ...(specialService
      ? {
          specialService: resolvableServiceForm2ApiMapper(
            withReadableValue(specialService),
          ),
        }
      : EMPTY_OBJECT),
    ...(resolvableServiceUsage.serviceUsageOrigin
      ? {
          serviceUsageOrigin: pruneInput(
            resolvableServiceUsage.serviceUsageOrigin,
            ServiceUsageOriginInput,
          ),
        }
      : EMPTY_OBJECT),
  };
}
